import { useEffect, useRef, useState } from "react";
import { useExperience } from "../../hooks/useExperience";
import { useTheme } from "../../hooks/useTheme";
import { track } from "../../lib/analytics";
import type { SceneController } from "./scene";
import { Arrow, Icon } from "../Icons";
import SculpturePoster from "./SculpturePoster";

export default function SceneView({
  kind = "sculpture",
}: {
  kind?: "sculpture" | "shoe";
}) {
  const { reducedMotion, paused, staticMode, toggleMotion } = useExperience();
  const { theme } = useTheme();
  const currentAccent = useRef<string>(theme.accent);
  currentAccent.current = theme.accent;
  const stage = useRef<HTMLDivElement>(null),
    host = useRef<HTMLDivElement>(null),
    controller = useRef<SceneController | null>(null);
  const [inView, setInView] = useState(false),
    [ready, setReady] = useState(false),
    [failed, setFailed] = useState(false);
  const [requested, setRequested] = useState(kind === "sculpture"),
    [separated, setSeparated] = useState(false);
  const still = reducedMotion || paused;
  const disabled = staticMode || reducedMotion || failed;
  useEffect(() => {
    if (!stage.current) return;
    const observer = new IntersectionObserver(
      (entries) => setInView(entries[0].isIntersecting),
      { rootMargin: kind === "sculpture" ? "100px" : "0px" },
    );
    observer.observe(stage.current);
    return () => observer.disconnect();
  }, [kind]);
  useEffect(() => {
    if (!inView || disabled || !requested || !host.current) return;
    let cancelled = false;
    const target = host.current;
    setReady(false);
    import("./scene")
      .then((module) => {
        if (cancelled) return;
        return module.createScene(target, {
          kind,
          motion: !still,
          accent: currentAccent.current,
          onReady: () => {
            if (!cancelled) setReady(true);
          },
          onError: () => {
            if (!cancelled) setFailed(true);
          },
          onInteract: () => track("hero_interaction", { scene: kind }),
        });
      })
      .then((scene) => {
        if (!scene) return;
        if (cancelled) scene.dispose();
        else {
          controller.current = scene;
          scene.setAccent(currentAccent.current);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      controller.current?.dispose();
      controller.current = null;
      setReady(false);
    };
    // Motion updates change the existing scene without recreating GPU resources.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, disabled, requested, kind]);
  useEffect(() => {
    controller.current?.setAccent(theme.accent);
  }, [theme.accent]);
  useEffect(() => {
    controller.current?.setMotion(!still);
  }, [still]);
  useEffect(() => {
    controller.current?.setSeparated(separated);
  }, [separated]);
  const status = disabled
    ? failed
      ? "Studio view · 3D unavailable"
      : "Studio view · static presentation"
    : ready
      ? "Live / WebGL"
      : requested && inView
        ? "Preparing the scene…"
        : "Studio view";
  return (
    <div
      className={`scene-view ${kind}-view`}
      ref={stage}
      data-ready={ready && !disabled}
      data-testid={`${kind}-view`}
    >
      <div className="scene-art">
        <div className="scene-poster">
          {kind === "sculpture" ? (
            <SculpturePoster />
          ) : (
            <img
              src="/images/projects/aero-live-studio.webp"
              alt="The Bone / Silver AERO/01 running shoe in a three-quarter studio image from the live website"
              width="1200"
              height="660"
              loading="lazy"
            />
          )}
        </div>
        {!disabled && (
          <div className="canvas-host" ref={host} aria-hidden="true" />
        )}
      </div>
      <div className="scene-status mono">
        <span className={ready ? "status-dot" : "quiet-dot"} />
        <span role="status">{status}</span>
      </div>
      {kind === "shoe" && !requested && !disabled && (
        <button
          className="button viewer-launch"
          onClick={() => setRequested(true)}
        >
          Explore in 3D <Arrow diagonal />
          <span className="sr-only">Downloads a 5.4 MB model</span>
        </button>
      )}
      {ready && !disabled && (
        <div
          className="scene-controls"
          aria-label={
            kind === "sculpture" ? "Sculpture controls" : "Shoe controls"
          }
        >
          <button
            onClick={() => controller.current?.rotate(-1)}
            aria-label="Rotate left"
          >
            <Icon name="left" />
          </button>
          <button
            onClick={() => controller.current?.rotate(1)}
            aria-label="Rotate right"
          >
            <Icon name="right" />
          </button>
          <button
            onClick={() => {
              controller.current?.reset();
              setSeparated(false);
            }}
          >
            Reset
          </button>
          {kind === "sculpture" && (
            <>
              <button
                aria-pressed={separated}
                onClick={() => setSeparated((v) => !v)}
              >
                {separated ? "Assemble" : "Separate"}
              </button>
              <button
                onClick={toggleMotion}
                aria-label={
                  paused ? "Resume sculpture motion" : "Pause sculpture motion"
                }
              >
                {paused ? "Play" : "Pause"}
              </button>
            </>
          )}
          {kind === "shoe" && (
            <button onClick={() => setRequested(false)}>Studio view</button>
          )}
        </div>
      )}
    </div>
  );
}
